import "styles/floatInput.less";

import {
    useState,
    ChangeEvent,
    useMemo,
    FC,
    useEffect,
} from "react";

import { Flex, Input, Select } from "antd";
import checkMark from "assets/images/checkmark.svg";
import britainFlag from "assets/images/form/gb-flag.svg";
import irelandFlag from "assets/images/form/ireland-flag.svg";
import warning from "assets/images/warning.svg";

import SuffixIcon from "../Suffix/Suffix";

interface PhoneInputWithCountryCodeProps {
    inputId: string;
    value?: string;
    error?: string;
    className?: string;
    dataTestId?: string;
    inputName?: string;
    disabled?: boolean;
    index?: number;
    onChange?: (_fullPhoneNumber: string) => void;
    handleKeyDown?: (_event: any) => void;
    suffix?: string;
}

const UkPhoneInputWithCountryCode: FC<PhoneInputWithCountryCodeProps> = ({
    value = "",
    error,
    onChange,
    disabled,
    index,
    dataTestId,
    inputName,
    handleKeyDown,
    suffix,
}) => {
    const firstSpaceIndex = value.indexOf(" ");

    const initialCountryCode = useMemo(() => {
        return firstSpaceIndex !== -1
            ? value.substring(0, firstSpaceIndex)
            : "+44";
    }, [value, firstSpaceIndex]);

    const initialPhoneNumber = useMemo(() => {
        return firstSpaceIndex !== -1
            ? value.substring(firstSpaceIndex + 1)
            : "";
    }, [value, firstSpaceIndex]);

    const [countryCode, setCountryCode] = useState(initialCountryCode);
    const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);

    const handleCountryCodeChange = (newCountryCode: string) => {
        setCountryCode(newCountryCode);
        if (onChange) {
            onChange(newCountryCode + " " + phoneNumber);
        }
    };

    const handlePhoneNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
        const newPhoneNumber = event.target.value;
        setPhoneNumber(newPhoneNumber);
        if (onChange) {
            onChange(countryCode + " " + newPhoneNumber);
        }
    };

    useEffect(() => {
        setCountryCode(initialCountryCode);
        setPhoneNumber(initialPhoneNumber);
    }, [initialCountryCode, initialPhoneNumber]);

    return (
        <Flex align="start" gap={5}>
            <Select
                id="country-code"
                disabled={disabled}
                value={countryCode}
                onChange={handleCountryCodeChange}
                size="large"
                style={{ width: 120 }}
            >
                <Select.Option className="phone-input-country-code-item" value="+44">
                    <div className="phone-input-country-code-select-item">
                        <img src={britainFlag} width={25} alt="UK flag" />
                        <p style={{ margin: "0 0 0 10px" }}>+44</p>
                    </div>
                </Select.Option>
                <Select.Option className="phone-input-country-code-item" value="+353">
                    <div className="phone-input-country-code-select-item">
                        <img src={irelandFlag} width={25} alt="Ireland flag" />
                        <p style={{ margin: "0 0 0 10px" }}>+353</p>
                    </div>
                </Select.Option>
            </Select>

            <Input
                id="phone-number"
                placeholder="Contact Number"
                value={phoneNumber}
                data-testid={dataTestId}
                name={inputName}
                onChange={handlePhoneNumberChange}
                disabled={disabled}
                autoFocus={index === 0}
                inputMode="numeric"
                onKeyDown={handleKeyDown}
                size="large"
                suffix={
                    <SuffixIcon
                        hasError={!!error}
                        hasValue={!!value}
                        suffixSrc={suffix}
                        checkmarkSrc={checkMark}
                        warningSrc={warning}
                    />
                }
            />
        </Flex>
    );
};

export default UkPhoneInputWithCountryCode;
